import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useHistory } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { BottomSheetModal } from '../../components/BottomSheetModal';
import { HeaderPopover } from '../../components/HeaderPopover';
import { Text } from '../../components/Text';
import { TabRadioButtons } from '../../components/TabRadioButtons';
import { CloseIcon, InfoIcon } from '../../theme/Icons';
import { ViewShareLinkButton } from '../ViewShareLinkButton';
import { SharingPeopleList } from './SharingPeopleList';
import { AddPersonFlow } from './AddPersonFlow';

const useStyles = createUseStyles({
    sheetWrap: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(90vh, 760px)',
        background: '#fff'
    },
    scrollArea: {
        padding: '20px 22px 16px',
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 14
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    divider: {
        height: 1,
        background: 'var(--ion-color-step-100)'
    },
    peopleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4
    },
    // ViewShareLinkButton renders its description as a default-size <p>; normalize it to
    // match the other section descriptions in this sheet.
    shareLinkWrap: {
        '& p': {
            fontSize: '14px !important',
            color: 'var(--ion-color-step-750) !important',
            lineHeight: 1.45,
            margin: '10px 0 0 !important'
        },
        '& p strong': {
            color: 'var(--ion-text-color)'
        }
    },
    popoverRole: {
        fontWeight: 700,
        color: 'var(--ion-text-color)'
    },
    // Pinned action footer: a sibling of the scroll area (not sticky-inside it) so the
    // scrolling content — including the visibility toggle's checkboxes — never bleeds over it.
    actionFooter: {
        flexShrink: 0,
        background: '#fff',
        padding: '12px 22px',
        borderTop: '1px solid var(--ion-color-step-100)'
    }
});

export interface ISharingAndAccessSheetProps {
    isOpen: boolean;
    // Which page to show when the sheet opens; an invite CTA can deep-link straight to 'add-person'.
    initialPage?: 'list' | 'add-person';
    onDidDismiss: () => void;
}

export const SharingAndAccessSheet: FC<ISharingAndAccessSheetProps> = ({ isOpen, initialPage = 'list', onDidDismiss }) => {
    const classes = useStyles();
    const history = useHistory();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);
    const community = communityFactory.forActiveCaremap();

    const mixpanel = useInstance(MixPanelEventEmitter);

    const [page, setPage] = useState<'list' | 'add-person'>('list');
    const [showAccessInfo, setShowAccessInfo] = useState(false);
    const [showShareLink, setShowShareLink] = useState(false);

    // Apply the requested initial page each time the sheet opens (handleClose resets to 'list').
    useEffect(() => {
        if (isOpen) {
            setPage(initialPage);
        }
    }, [isOpen, initialPage]);

    const caremap = careMapState.getActiveCaremap();
    const patient = caremap?.patient;

    if (!patient) {
        return null;
    }

    const isShared = patient.notesIsPublic === true;

    const loaded = community.isLoaded();
    const hasAnyone = loaded && (community.getPermanentCoOwners().length > 0 || community.getPermanentViewers().length > 0 || community.getInvites('co-owner').length > 0 || community.getInvites('view-only').length > 0);

    const handleClose = () => {
        setPage('list');
        setShowAccessInfo(false);
        setShowShareLink(false);
        onDidDismiss();
    };

    const updateVisibilityDefault = (shared: boolean) => {
        if (shared !== isShared) {
            mixpanel.track('Default Note Visibility Changed', () => ({ 'State': shared ? 'shared' : 'private' }));
            careMapState.updatePatient({ ...patient, notesIsPublic: shared });
        }
    };

    const openMemberInCareMap = (memberId: string) => {
        const link = careMapState.maybeGetMemberLink(memberId);
        if (!caremap || !link) {
            return;
        }
        const path = link.hiveKey === 'household'
            ? `/caremaps/${caremap.careMapId}/drawer/household/member/${memberId}/edit`
            : `/caremaps/${caremap.careMapId}/drawer/support-group/${link.hiveKey}/member/${memberId}/edit`;
        // Close the sheet first, then navigate once its modal has dismissed — otherwise the
        // drawer mounts under the open modal and the caremap context is briefly unavailable.
        handleClose();
        setTimeout(() => history.push(path), 350);
    };

    return (
        <>
            <BottomSheetModal isOpen={isOpen} onDidDismiss={handleClose} backdropDismiss={false} data-id="sharing-and-access-sheet">
                <div className={classes.sheetWrap}>
                    {page === 'list' && (
                        <>
                            <div className={classes.scrollArea}>
                                <div className={classes.headerRow}>
                                    <Text type="title">Sharing & Access</Text>
                                    <IonButton fill="clear" color="medium" onClick={handleClose} aria-label="Close" data-id="close-button">
                                        <CloseIcon />
                                    </IonButton>
                                </div>

                                <div className={classes.peopleHeader}>
                                    <Text type="subtitle">People with access</Text>
                                    <IonButton id="access-info-trigger" fill="clear" color="medium" size="small" onClick={() => setShowAccessInfo(v => {
                                        if (!v) {
                                            mixpanel.track('Tooltip Opened', () => ({ Tooltip: 'Access Levels' }));
                                        }
                                        return !v;
                                    })} aria-label="About access levels"><InfoIcon /></IonButton>
                                </div>
                                {hasAnyone && (
                                    <Text type="detail">
                                        Viewers see shared notes and recordings. Co-Owners have full access and can invite others.
                                    </Text>
                                )}

                                {!loaded || hasAnyone ? (
                                    <SharingPeopleList onUpdateInCareMap={openMemberInCareMap} />
                                ) : (
                                    <Text type="detail">
                                        No one else has access yet. Invite others when you're ready to start collaborating!
                                    </Text>
                                )}

                                <Text type="subtitle">Share Temporary Access</Text>
                                {showShareLink && (
                                    <div className={classes.shareLinkWrap}>
                                        <ViewShareLinkButton trackingFrom="Sharing Set Up" />
                                    </div>
                                )}
                                <IonButton fill="outline" size="small" onClick={() => setShowShareLink(v => !v)}>
                                    {showShareLink ? 'Done' : '+ Create Link'}
                                </IonButton>

                                <div className={classes.divider} />

                                <Text type="subtitle">Default Note & Recording Access for Viewers</Text>
                                <TabRadioButtons
                                    value={isShared}
                                    onChange={updateVisibilityDefault}
                                    buttons={[
                                        { label: 'Shared', value: true },
                                        { label: 'Hidden', value: false }
                                    ]}
                                />
                                <Text type="detail">
                                    {isShared
                                        ? 'New notes and recordings are visible to you, viewers, and co-owners. You can still hide any individual note from viewers.'
                                        : 'New notes and recordings stay between you and any co-owners. You choose what to share with viewers.'}
                                </Text>
                            </div>

                            <div className={classes.actionFooter}>
                                <IonButton expand="block" onClick={() => setPage('add-person')} data-id="add-person-btn">
                                    Invite Someone
                                </IonButton>
                            </div>
                        </>
                    )}

                    {page === 'add-person' && (
                        <AddPersonFlow
                            onCancel={() => setPage('list')}
                            onDone={() => setPage('list')}
                            onClose={handleClose}
                            patientFirstName={patient.firstName || 'them'}
                        />
                    )}
                </div>
            </BottomSheetModal>

            <HeaderPopover
                trigger="access-info-trigger"
                isOpen={showAccessInfo}
                onDidDismiss={() => setShowAccessInfo(false)}
                maxWidth="default"
                title="Sharing Options"
                content={
                    <Text type="detail">
                        <div>
                            <span className={classes.popoverRole}>Viewer</span> — sees shared notes, recordings, and appointments, and can comment. Can't edit anything or see hidden info (hidden notes, recordings, medications, diagnoses).
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <span className={classes.popoverRole}>Co-Owner</span> — a full partner. Sees everything, including hidden notes, recordings, medications, and diagnoses. Can add and edit notes, recordings, appointments, and tasks, and can invite and remove others. Two included in free.
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <span className={classes.popoverRole}>Share link</span> — temporary access without needing an account. Sees shared notes, recordings, and appointments but can't comment.
                        </div>
                    </Text>
                }
            />
        </>
    );
};
